<template>
  <div :style="`height: ${height}px; overflow-y: auto; ${backgroundStyle}`">
    <div class="w-100 p-3 d-flex align-items-center justify-content-between"
      style="background-color: rgba(250, 250, 250, 0.7);">
      <span>
        <strong>{{ $t("js.backend.ballot_preview.interactive_preview", backendLocale) }}</strong>
        <i class="fas fa-info-circle text-dark ms-1 cursor-pointer" data-bs-toggle="modal"
          data-bs-target="#ballotPreviewModal"></i>
      </span>

      <div class="d-flex gap-2">
        <button :class="`btn btn-sm ${contestsOpened ? 'btn-danger' : 'btn-success'}`" @click="toggleContestsOpened"
          v-text="contestsOpened ? $t('js.backend.ballot_preview.close_ballot', backendLocale) : $t('js.backend.ballot_preview.open_ballot', backendLocale)" />
        <select class="btn btn-sm btn-dark dropdown-toggle" v-model="selectedLocale">
          <option v-for="locale in availableLocales" :key="`${locale}-locale`" :value="locale">{{ $t("js.language_name", locale) }}</option>
        </select>
      </div>
    </div>

    <div class="p-0 m-0">
      <div class="container p-0 px-md-5 pt-sm-3 pt-md-5 pb-5">
        <div class="card border-0 rounded-0 shadow-lg h-100 mb-3">
          <div
            class="card-header d-flex flex-column flex-sm-row align-items-stretch bg-theme rounded-0 border-bottom align-items-sm-center p-0">
            <div v-if="themeImg" class="bg-light p-3 align-self-stretch d-flex align-items-center">
              <img :src="themeImg" class="me-3" style="max-height: 50px; max-width: 100%" />
            </div>
            <div class="d-flex flex-grow-1 align-items-center justify-content-between p-3">
              <h3 class="mb-0">{{ electionTitle[$i18n.locale] }}</h3>
            </div>
          </div>
          <div class="card-body h-100 p-3 p-md-5" :dir="isRtl ? 'rtl' : 'ltr'">
            <template v-if="slide.data">
              <header v-if="slide.data.title" class="h1 text-center">{{ slide.data.title[$i18n.locale] }}</header>
              <div v-if="slide.data.description" class="lead" v-html="slide.data.description[$i18n.locale]" />
            </template>

            <template v-if="contestsOpened">
              <AVSplitHelper
                :image-option='imageOption'
                :key="contests[contestIndex].reference"
                :contest="formatContest(contests[contestIndex])"
                :contestSelection="activeContestSelection"
                :partial-results="partialResults"
                :includeLazyErrors="includeLazyErrors"
                :weight="1"
                :show-submission-helper="true"
                @update:contestSelection="updateActiveContestSelection"
                @update:complete="updateCompleted"
                @update:activePile="updateActivePile"
              />

              <div class="d-flex mt-3 gap-3 flex-column-reverse flex-md-row">
                <button
                  v-if="contestIndex > 0"
                  class="btn btn-lg btn-ballot-outline col-12 col-md-auto"
                  @click="goBack"
                  v-text="$t('js.election_client.actions.back')"
                />
                <button
                  v-if="!isLastContest"
                  class="btn btn-lg btn-theme ms-auto col-12 col-md-auto"
                  @click="goForward"
                  v-text="$t('js.election_client.actions.continue')"
                />
              </div>
            </template>

            <template v-else>
              <ContestHeader
                v-for="contest in orderedContests"
                :contest="(formatContest(contest) as unknown as ConferenceContest)"
                :show-options="true"
                :locale="$i18n.locale"
              />
              <hr>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="modal fade" id="ballotPreviewModal" tabindex="-1"
    :aria-labelledby="$t('js.backend.ballot_preview.interactive_preview', backendLocale)" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable">
      <div class="modal-content">
        <div class="modal-header">
          <h4 class="modal-title fs-5">{{ $t("js.backend.ballot_preview.modal.title", backendLocale) }}</h4>
          <button type="button" class="btn-close" data-bs-dismiss="modal"
            :aria-label="$t('backend.shared.modal.close', backendLocale)"></button>
        </div>
        <div class="modal-body">
          <p><strong>{{ $t("js.backend.ballot_preview.modal.subtitle", backendLocale) }}</strong></p>
          <div class="alert alert-warning my-4">
            <i class="fas fa-exclamation-triangle me-1"></i>
            {{ $t("js.backend.ballot_preview.modal.alert", backendLocale) }}
          </div>
          <p><strong>{{ $t("js.backend.ballot_preview.modal.what_to_check", backendLocale) }}</strong></p>
          <p>{{ $t("js.backend.ballot_preview.modal.examples", backendLocale) }}</p>
          <ul>
            <li>{{ $t("js.backend.ballot_preview.modal.example_1", backendLocale) }}</li>
            <li>{{ $t("js.backend.ballot_preview.modal.example_2", backendLocale) }}</li>
            <li>{{ $t("js.backend.ballot_preview.modal.example_3", backendLocale) }}</li>
            <li>{{ $t("js.backend.ballot_preview.modal.example_4", backendLocale) }}</li>
            <li>{{ $t("js.backend.ballot_preview.modal.example_5", backendLocale) }}</li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import ContestHeader from "@/components/shared/ContestHeader.vue";
import { formatContest, orderContests, flattenOptions } from "@/entrypoints/shared/contest_utilities";
import { rtlLanguages } from "@/entrypoints/shared/i18n_constants";
import type {
  PropType,
  ConferenceContest,
  Slide,
  LocalString,
  Locale,
  SelectionPile,
  ContestSelection,
  ConferenceOption
} from "@/types";

export default defineComponent({
  name: "BallotPreview",
  components: { ContestHeader },
  props: {
    contests: {
      type: Array as PropType<ConferenceContest[]>,
      required: true,
    },
    contestPositions: {
      type: Object,
      required: true,
    },
    slide: {
      type: Object as PropType<Slide>,
      required: true,
    },
    electionTitle: {
      type: Object,
      required: true,
    },
    themeImg: {
      type: String,
      default: null,
    },
    backgroundStyle: {
      type: String,
      default: null,
    },
    height: {
      type: Number,
      default: null,
    },
    votingRoundTitles: {
      type: Object as PropType<LocalString>,
      required: true,
    },
    initialLocale: {
      type: String as PropType<Locale>,
      default: "en",
    },
    backendLocale: {
      type: String as PropType<Locale>,
      default: "en",
    },
    availableLocales: {
      type: Array as PropType<Locale[]>,
      default: () => ["en"],
    },
    imageOption: {
      type: String,
      default: null
    }
  },
  data({ initialLocale }) {
    return {
      activeSplitState: "ballot",
      contestIndex: 0,
      isLastContest: false,
      confirming: false,
      contestsOpened: false,
      partialResults: null,
      selectedLocale: initialLocale,
      includeLazyErrors: false,
      activePile: null,
      ballotSelection: null,
    }
  },
  computed: {
    orderedContests() {
      return orderContests(this.contests, this.contestPositions);
    },
    title() {
      return this.votingRoundTitles[this.$i18n.locale];
    },
    activeContestSelection() {
      return this.ballotSelection.contestSelections[this.contestIndex];
    },
    isRtl() {
      return rtlLanguages.includes(this.$i18n.locale);
    },
  },
  methods: {
    formatContest,
    orderContests,
    flattenOptions,
    goBack() {
      this.includeLazyErrors = false;
      this.contestIndex--;
      this.confirming = false;
      this.isLastContest = this.contestIndex + 1 == this.contests.length;
    },
    goForward() {
      this.includeLazyErrors = false;
      this.contestIndex++;
      this.isLastContest = this.contestIndex + 1 == this.contests.length;
    },
    toggleContestsOpened() {
      this.contestsOpened = !this.contestsOpened;
    },
    updateCompleted(completed: boolean) {
      if (completed) this.includeLazyErrors = true;
    },
    updateActivePile(newPile: SelectionPile) {
      this.activePile = newPile;
    },
    updateActiveContestSelection(contestSelection: ContestSelection) {
      this.ballotSelection.contestSelections.splice(
        this.contestIndex,
        1,
        contestSelection,
      );
    },
  },
  watch: {
    selectedLocale() {
      this.$i18n.locale = this.selectedLocale;
    }
  },
  created() {
    this.$i18n.locale = this.selectedLocale;
    this.isLastContest = this.contests.length == 1 ? true : false;

    this.ballotSelection = {
      reference: "dummy",
      contestSelections: this.orderedContests.map((contest: ConferenceContest) => {
        return {
          reference: contest.reference,
          piles: [],
        }
      }),
    }

    if (this.slide.hand_raise) {
      const initial = {
        blank: {
          reference: "blank",
          results: { count: 0, percentage: 0 },
        },
      };

      this.flattenOptions(this.contests[0].options).forEach((option: ConferenceOption) => {
        initial[option.reference] = {
          reference: option.reference,
          results: { count: 0, percentage: 0 },
        };
      });

      this.partialResults = initial;
    }
  },
});
</script>
